import { useCallback, useMemo } from 'react';
import { LogicalSpace, TrafficRule } from '@thive/fddm-types';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { HistoryServiceFactory } from '@/modules/canvas/services/history/HistoryServiceFactory';
import { isAtLeast1 } from '@/modules/floorplanDesign/helpers/minItems';

/** @description True when the rules allow the given mode for the given vehicle type. */
export const hasVehicleMode = (
  rules: TrafficRule[],
  vehicleTypeId: string,
  mode: string,
): boolean =>
  rules.some(
    (rule) =>
      rule.type === 'VEHICLE_MODE' &&
      rule.vehicleModeExtension?.vehicleTypeId === vehicleTypeId &&
      rule.vehicleModeExtension.vehicleModes.includes(mode),
  );

/**
 * @description Returns the rules with the vehicle mode turned on or off. Returns the same array
 * when nothing changes, so unchanged logical spaces are not updated.
 */
const withVehicleMode = (
  rules: TrafficRule[],
  vehicleTypeId: string,
  mode: string,
  enabled: boolean,
): TrafficRule[] => {
  const existingRuleIndex = rules.findIndex(
    (rule) =>
      rule.type === 'VEHICLE_MODE' && rule.vehicleModeExtension?.vehicleTypeId === vehicleTypeId,
  );

  if (existingRuleIndex === -1) {
    if (!enabled) return rules;

    return [
      ...rules,
      {
        type: 'VEHICLE_MODE',
        vehicleModeExtension: {
          vehicleTypeId,
          vehicleModes: [mode],
        },
      },
    ];
  }

  const currentRule = rules[existingRuleIndex];
  const currentExtension = currentRule.vehicleModeExtension;

  if (!currentExtension) return rules;
  if (currentExtension.vehicleModes.includes(mode) === enabled) return rules;

  const nextModes = enabled
    ? [...currentExtension.vehicleModes, mode]
    : currentExtension.vehicleModes.filter((m) => m !== mode);

  if (!isAtLeast1(nextModes)) {
    return rules.filter((_, index) => index !== existingRuleIndex);
  }

  return rules.map((rule, index) =>
    index === existingRuleIndex
      ? {
          type: 'VEHICLE_MODE',
          vehicleModeExtension: {
            vehicleTypeId,
            vehicleModes: nextModes,
          },
        }
      : rule,
  );
};

export function useLogicalSpaceTrafficRuleActions(logicalSpaces: LogicalSpace[]) {
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const historySvc = useMemo(() => HistoryServiceFactory.getInstance().createHistoryService(), []);

  /** Applies the updater to every logical space and saves the changed ones as one undo step. */
  const updateTrafficRules = useCallback(
    (updater: (rules: TrafficRule[]) => TrafficRule[]) => {
      const nextLogicalSpaces = logicalSpaces.flatMap((logicalSpace): LogicalSpace[] => {
        const nextTrafficRules = updater(logicalSpace.trafficRules);
        if (nextTrafficRules === logicalSpace.trafficRules) return [];

        return [{ ...logicalSpace, trafficRules: nextTrafficRules }];
      });

      if (nextLogicalSpaces.length === 0) return;

      historySvc.batch(() => {
        nextLogicalSpaces.forEach((logicalSpace) => fddmSvc.updateLogicalSpace(logicalSpace));
      });
    },
    [fddmSvc, historySvc, logicalSpaces],
  );

  const setDoNotStopEnabled = useCallback(
    (enabled: boolean) => {
      updateTrafficRules((rules) => {
        const exists = rules.some((rule) => rule.type === 'DO_NOT_STOP');

        if (enabled) {
          if (exists) return rules;
          return [...rules, { type: 'DO_NOT_STOP' }];
        }

        if (!exists) return rules;
        return rules.filter((rule) => rule.type !== 'DO_NOT_STOP');
      });
    },
    [updateTrafficRules],
  );

  const setRestrictedAccessEnabled = useCallback(
    (enabled: boolean) => {
      updateTrafficRules((rules) => {
        const exists = rules.some((rule) => rule.type === 'RESTRICTED_ACCESS');

        if (enabled) {
          if (exists) return rules;
          return [...rules, { type: 'RESTRICTED_ACCESS' }];
        }

        if (!exists) return rules;
        return rules.filter((rule) => rule.type !== 'RESTRICTED_ACCESS');
      });
    },
    [updateTrafficRules],
  );

  const setMaxSpeed = useCallback(
    (value: number | '') => {
      updateTrafficRules((rules) => {
        const nextValue = value === '' ? undefined : value;
        const existingRuleIndex = rules.findIndex((rule) => rule.type === 'SPEED_LIMIT');

        if (nextValue === undefined) {
          if (existingRuleIndex === -1) return rules;
          return rules.filter((rule) => rule.type !== 'SPEED_LIMIT');
        }

        const nextRule: TrafficRule = {
          type: 'SPEED_LIMIT',
          speedLimitExtension: {
            maxSpeed: nextValue,
          },
        };

        if (existingRuleIndex === -1) {
          return [...rules, nextRule];
        }

        const currentRule = rules[existingRuleIndex];
        if (currentRule.speedLimitExtension?.maxSpeed === nextValue) return rules;

        const nextRules = [...rules];
        nextRules[existingRuleIndex] = nextRule;
        return nextRules;
      });
    },
    [updateTrafficRules],
  );

  // On a multi-selection a mode that not every logical space has is shown as off,
  // so toggling it turns it on for all; a mode that every space has is turned off for all.
  const toggleVehicleMode = useCallback(
    (vehicleTypeId: string, mode: string) => {
      if (!vehicleTypeId || !mode) return;

      const enabled = !logicalSpaces.every(({ trafficRules }) =>
        hasVehicleMode(trafficRules, vehicleTypeId, mode),
      );

      updateTrafficRules((rules) => withVehicleMode(rules, vehicleTypeId, mode, enabled));
    },
    [logicalSpaces, updateTrafficRules],
  );

  return {
    setDoNotStopEnabled,
    setRestrictedAccessEnabled,
    setMaxSpeed,
    toggleVehicleMode,
  };
}
